import { config } from '@/config/env';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * REJESTR PRODUKTÓW TUSZAMY — źródło prawdy dla globalnego przełącznika aplikacji
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * DWA POZIOMY NAWIGACJI, KTÓRYCH NIE WOLNO MIESZAĆ:
 *   POZIOM 1 — produkty TuSzamy (Control Center, Admin, CRM, Drive, …) — TEN plik
 *   POZIOM 2 — ekrany wewnątrz Control Center — app/workspaces.ts
 * CRM i Drive to OSOBNE PRODUKTY, a nie zakładki analityczne Control Center. Pasek, w którym
 * „CRM" stoi obok „Raporty", mówi użytkownikowi coś fałszywego o architekturze firmy.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────────┐
 * │ JAK USTALONO ADRESY — i dlaczego większość ma status NIEPODŁĄCZONY                        │
 * │                                                                                          │
 * │ `*.tuszama.pl` ma WILDCARD DNS: każda nazwa, łącznie z całkowicie zmyśloną, rozwiązuje   │
 * │ się na 188.210.222.8. Sprawdzone wprost — `nieistniejacy-losowy-test-9q7x.tuszama.pl`    │
 * │ też się rozwiązuje. Samo „domena odpowiada" NIE JEST więc dowodem, że produkt tam stoi.  │
 * │                                                                                          │
 * │ Za zweryfikowane uznajemy wyłącznie adresy o WŁASNYM, rozpoznanym adresie IP albo takie, │
 * │ z którymi ta sesja realnie rozmawiała. Reszta dostaje NIEPODŁĄCZONY — bo fałszywy        │
 * │ odnośnik w launcherze jest gorszy niż jego brak: prowadzi właściciela w pustkę i podważa │
 * │ zaufanie do całego panelu.                                                               │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * UWIERZYTELNIANIE: TuSzama NIE MA dziś wspólnego logowania między produktami. Launcher jest
 * nawigacją, nie SSO. Każdy kafel mówi wprost, czy przejście oznacza ponowne logowanie —
 * udawanie bezszwowego przejścia byłoby obietnicą, której produkt nie dotrzyma.
 */

export type Dostepnosc =
  /** Adres zweryfikowany dla BIEŻĄCEGO środowiska — odnośnik działa. */
  | 'DOSTEPNY'
  /** Produkt istnieje, ale nie ma potwierdzonego adresu dla tego środowiska. */
  | 'NIEPODLACZONY'
  /** Produkt jeszcze nie powstał jako osobny panel. */
  | 'PLANOWANY';

export type ModelUwierzytelniania =
  | 'WSPOLNA_SESJA'
  | 'OSOBNE_LOGOWANIE'
  | 'NIEUSTALONY';

export interface Produkt {
  id: string;
  nazwa: string;
  /** Jedno zdanie: po co ten produkt istnieje. Nie nazwa modułu, tylko zadanie. */
  rola: string;
  /** Dwie litery na kafel — znak zamiast ikony z biblioteki, spójny z monogramem marki. */
  monogram: string;
  /** Barwa akcentu kafla; wyłącznie tokeny palety serii, żeby launcher nie wprowadzał nowych barw. */
  akcent: string;
  urlTest: string | null;
  urlProd: string | null;
  uwierzytelnianie: ModelUwierzytelniania;
  /** Skąd wiadomo, że adres jest prawdziwy. Puste = nie zweryfikowano. */
  dowodAdresu: string;
  biezacy?: boolean;
}

export const PRODUKTY: readonly Produkt[] = [
  {
    id: 'control-center',
    nazwa: 'Control Center',
    rola: 'Analityka i stan całej TuSzamy w jednym miejscu',
    monogram: 'CC',
    akcent: 'var(--zloto)',
    urlTest: 'https://panel.test.tuszama.pl/cc/',
    urlProd: null, // Control Center nie jest jeszcze wdrożony na produkcji.
    uwierzytelnianie: 'WSPOLNA_SESJA',
    dowodAdresu: 'Ta aplikacja — adres potwierdzony wdrożeniem i publicznym E2E.',
    biezacy: true,
  },
  {
    id: 'admin',
    nazwa: 'TuSzama Admin',
    rola: 'Panel administracyjny głównej aplikacji — konta, szkoły, zamówienia',
    monogram: 'AD',
    akcent: 'var(--seria-2)',
    // Zweryfikowane w tej sesji: ten host zwraca panel administracyjny (tytuł „TuSzamaPanel").
    urlTest: 'https://panel.test.tuszama.pl/',
    // PROD-owego adresu panelu NIE zweryfikowano. `panel.tuszama.pl` rozwiązuje się wyłącznie
    // przez wildcard, więc wpisanie go tutaj byłoby zgadywaniem.
    urlProd: null,
    uwierzytelnianie: 'OSOBNE_LOGOWANIE',
    dowodAdresu: 'Odpowiedź HTTP z tego hosta w tej sesji: <title>TuSzamaPanel</title>.',
  },
  {
    id: 'crm',
    nazwa: 'CRM',
    rola: 'Kampanie, powiadomienia i kartoteka klienta',
    monogram: 'CR',
    akcent: 'var(--seria-3)',
    // Na TEST stoi wyłącznie izolowana instancja CRM obsługująca Control Center; nie jest to
    // panel dla właściciela, więc nie podajemy go jako produktu.
    urlTest: null,
    urlProd: 'https://crm.tuszama.pl',
    uwierzytelnianie: 'OSOBNE_LOGOWANIE',
    dowodAdresu: 'Własny adres IP 165.22.30.143 (rozpoznany serwer CRM), poza wildcardem domeny.',
  },
  {
    id: 'drive',
    nazwa: 'TuSzama Drive',
    rola: 'Aplikacja kierowców i podgląd dostaw',
    monogram: 'DR',
    akcent: 'var(--seria-5)',
    urlTest: null, // Środowisko TEST Drive nie ma publicznego adresu dla właściciela.
    urlProd: 'https://kierowcy.tuszama.pl',
    uwierzytelnianie: 'OSOBNE_LOGOWANIE',
    dowodAdresu: 'Własny adres IP 165.22.30.143, adres używany operacyjnie i obecny w repozytorium.',
  },
  {
    id: 'restauracja',
    nazwa: 'Restauracja',
    rola: 'Panel restauratora — karta dań i realizacja zamówień',
    monogram: 'RE',
    akcent: 'var(--seria-4)',
    urlTest: null,
    urlProd: null,
    uwierzytelnianie: 'NIEUSTALONY',
    // Rola restauratora działa wewnątrz głównej aplikacji, a nie pod własnym adresem.
    // `restauracja.tuszama.pl` rozwiązuje się tylko przez wildcard — to nie jest dowód.
    dowodAdresu: '',
  },
  {
    id: 'bot-reklamacji',
    nazwa: 'Bot Reklamacji',
    rola: 'Obsługa zgłoszeń i opinii klientów',
    monogram: 'BR',
    akcent: 'var(--seria-6)',
    urlTest: null,
    urlProd: null,
    uwierzytelnianie: 'NIEUSTALONY',
    // Istnieje usługa przetwarzająca opinie z poczty, ale jest to proces w tle, a nie panel
    // z interfejsem. Dopóki nie ma ekranu, do którego można wejść, nie ma czego linkować.
    dowodAdresu: '',
  },
];

/** Adres właściwy dla ŚRODOWISKA, w którym stoi ta instancja Control Center.
 *  Nigdy nie kierujemy z TEST na produkcję — to najprostsza droga do pomyłki na realnych danych. */
export function adresProduktu(p: Produkt): string | null {
  if (config.environment === 'prod') return p.urlProd;
  if (config.environment === 'test') return p.urlTest;
  return null;
}

export function dostepnoscProduktu(p: Produkt): Dostepnosc {
  if (adresProduktu(p)) return 'DOSTEPNY';
  if (p.urlTest || p.urlProd) return 'NIEPODLACZONY';
  return 'PLANOWANY';
}

export const OPIS_DOSTEPNOSCI: Record<Dostepnosc, string> = {
  DOSTEPNY: 'otwiera się',
  NIEPODLACZONY: 'brak adresu dla tego środowiska',
  PLANOWANY: 'nie ma jeszcze osobnego panelu',
};
